// V3.4 (#2, #3): shared, pure view-model builders consumed by BOTH renderers
// so the executive summary and the threat heatmap are byte-aligned between the
// PDF (which works off `profile`/`input`/`framework` directly) and the DOCX
// (which works off prepared ReportData). No React, no @react-pdf, no docx.

// V4.2 — the user-facing 0–100 calibration. The report layer takes no
// ColorConfig, so it uses the default-bound calibration; if admin threshold
// overrides are ever plumbed through `PreparationInputs`, this is the one line
// that has to become `makeColorFns(colors).toScore100`.
import { toScore100 as score100 } from "../ui/utils";
import { THREATS } from "../taxonomy";
import { cellExposureMulti } from "../scoring";
import { computeMaturityStage, type IndustryProfileId } from "../scoring/maturity-stage";
import { formatDomain, formatMaturityLevel } from "../shared/labels";
import type {
  CiscoProductMapping,
  ConsolidationMode,
  Framework,
  MultiAssessmentInput,
  MultiRiskProfile,
} from "../shared/types";

export type ExecutiveSummary = {
  /** One-paragraph plain-English posture statement. */
  narrative: string;
  score: number;
  posture: string;
  useCaseCount: number;
  topThreats: Array<{ name: string; exposureScore: number; likelihoodPct: number; impactPct: number; severity: string }>;
  biggestGaps: Array<{
    domainLabel: string;
    currentLabel: string;
    targetLabel: string;
    gap: number;
  }>;
  /** Top recommendations, framed as the highest-leverage moves. */
  priorityMoves: Array<{
    controlName: string;
    domainLabel: string;
    expectedScoreDrop: number;
  }>;
  nextSteps: string[];
  /** Weighted-workstream maturity Stage. */
  maturityStage: {
    stage: number;
    label: string;
    cappedBy: string;
  };
};

// Deliberately duck-typed (not imported from ./types) to avoid a circular
// import — report/types.ts imports ExecutiveSummary/ThreatMatrix FROM this
// file, so this file can't import RecommendationDetail back from types.ts.
// RecommendationDetail already satisfies this shape structurally.
type ProductExposureInput = {
  controlId: string;
  controlName: string;
  domainLabel: string;
  expectedScoreDrop: number;
  threatsAddressed: Array<{ name: string }>;
};

export type ProductExposureRow = {
  productId: string;
  productName: string;
  productShortName: string;
  productUrl: string;
  controlsAddressed: Array<{ controlId: string; controlName: string; domainLabel: string }>;
  threatsAddressed: string[];
  totalExpectedScoreDrop: number;
};

/**
 * Flips the existing "control -> one product" lookup (surfaced today as a
 * badge on each recommendation card) into a "product -> every control and
 * threat it closes, ranked by risk-reduction" view — the deeper Cisco-
 * product lens the report needs. Pure aggregation over already-computed
 * recommendations; no new scoring concept, so it belongs here alongside the
 * other shared PDF/DOCX/on-screen view-model builders, not in the scoring
 * engine itself.
 */
export function buildProductExposure(
  recommendations: ProductExposureInput[],
  ciscoMappings: Record<string, CiscoProductMapping[]>,
): ProductExposureRow[] {
  const byProduct = new Map<string, ProductExposureRow>();

  for (const rec of recommendations) {
    const mappings = ciscoMappings[rec.controlId] ?? [];
    for (const mapping of mappings) {
      const existing = byProduct.get(mapping.product.id);
      const row: ProductExposureRow =
        existing ??
        {
          productId: mapping.product.id,
          productName: mapping.product.name,
          productShortName: mapping.product.shortName,
          productUrl: mapping.product.url,
          controlsAddressed: [],
          threatsAddressed: [],
          totalExpectedScoreDrop: 0,
        };

      if (!row.controlsAddressed.some((c) => c.controlId === rec.controlId)) {
        row.controlsAddressed.push({
          controlId: rec.controlId,
          controlName: rec.controlName,
          domainLabel: rec.domainLabel,
        });
        row.totalExpectedScoreDrop += rec.expectedScoreDrop;
      }
      for (const threat of rec.threatsAddressed) {
        if (!row.threatsAddressed.includes(threat.name)) row.threatsAddressed.push(threat.name);
      }

      byProduct.set(mapping.product.id, row);
    }
  }

  return [...byProduct.values()].sort((a, b) => b.totalExpectedScoreDrop - a.totalExpectedScoreDrop);
}

export type ThreatMatrix = {
  columns: Array<{ id: string; label: string }>;
  rows: Array<{
    threatId: string;
    name: string;
    shortLabel: string;
    /** Exposure 0..1 per column (domain), or null where the threat is unmapped. */
    cells: Array<number | null>;
  }>;
};

function threatName(threatId: string): string {
  return THREATS.find((t) => t.id === threatId)?.name ?? threatId;
}

export function buildExecutiveSummary(
  profile: MultiRiskProfile,
  useCaseCount: number,
  framework: Framework,
  orgName?: string,
  industryProfile?: string,
): ExecutiveSummary {
  const c = profile.consolidated;
  const score = score100(c.overall);
  const posture = c.overallLabel;

  const pillar = (id: string) =>
    c.domainScores.find((d) => d.domainId === id)?.current ?? 0;
  const stage = computeMaturityStage(
    { D1: pillar("D1"), D2: pillar("D2"), D3: pillar("D3") },
    (industryProfile as IndustryProfileId) ?? "default",
  );

  const topThreats = c.topThreats.slice(0, 3).map((t) => ({
    name: threatName(t.threatId),
    exposureScore: score100(t.exposure),
    likelihoodPct: Math.round(t.likelihood * 100),
    impactPct: Math.round(t.impact * 100),
    severity: t.severity,
  }));

  const biggestGaps = [...c.domainScores]
    .sort((a, b) => b.target - b.current - (a.target - a.current))
    .slice(0, 2)
    .map((d) => {
      const dom = framework.domains.find((x) => x.id === d.domainId);
      return {
        domainLabel: formatDomain(d.domainId, dom?.name),
        currentLabel: formatMaturityLevel(d.current),
        targetLabel: formatMaturityLevel(d.target),
        gap: Math.max(0, d.target - d.current),
      };
    });

  const priorityMoves = c.recommendations.slice(0, 3).map((r) => {
    const dom = framework.domains.find((x) => x.id === r.domainId);
    return {
      controlName: r.controlName,
      domainLabel: formatDomain(r.domainId, dom?.name),
      expectedScoreDrop: Math.round(r.expectedScoreDrop),
    };
  });

  const who = orgName?.trim() ? `${orgName.trim()}'s` : "Your";
  const ucPhrase = `${useCaseCount} technology domain${useCaseCount === 1 ? "" : "s"}`;
  const threatPhrase =
    topThreats.length > 0
      ? `, driven mainly by ${topThreats
          .slice(0, 2)
          .map((t) => t.name)
          .join(" and ")}`
      : "";
  const gapPhrase = biggestGaps[0]
    ? ` The widest maturity gap is in ${biggestGaps[0].domainLabel}.`
    : "";
  const movePhrase =
    priorityMoves.length > 0
      ? ` Acting on the ${priorityMoves.length} prioritised control${priorityMoves.length === 1 ? "" : "s"} below — starting with ${priorityMoves[0].controlName} — is expected to deliver the largest reduction in exposure.`
      : "";
  const narrative = `${who} infrastructure footprint spans ${ucPhrase}. Consolidated risk is ${score} / 100 (${posture})${threatPhrase}.${gapPhrase}${movePhrase}`;

  const nextSteps: string[] = [];
  if (priorityMoves.length > 0) {
    nextSteps.push(
      `Prioritise the controls below — beginning with ${priorityMoves[0].controlName} — to cut the most risk for the least effort.`,
    );
  }
  if (topThreats.length > 0) {
    nextSteps.push(
      `Harden against the highest-exposure threats (${topThreats
        .map((t) => t.name)
        .join(", ")}) with targeted testing and runtime guardrails.`,
    );
  }
  nextSteps.push(
    "Re-run this assessment after closing the gaps to confirm the risk reduction and report progress to stakeholders.",
  );

  return {
    narrative,
    score,
    posture,
    useCaseCount,
    topThreats,
    biggestGaps,
    priorityMoves,
    nextSteps,
    maturityStage: {
      stage: stage.finalStage,
      label: stage.finalLabel,
      cappedBy: stage.cappedBy,
    },
  };
}

export function buildThreatMatrix(
  profile: MultiRiskProfile,
  input: MultiAssessmentInput,
  framework: Framework,
  mode: ConsolidationMode,
  limit = 12,
): ThreatMatrix {
  const columns = framework.domains.map((d) => ({
    id: d.id,
    label: formatDomain(d.id, d.name),
  }));

  const rows = profile.consolidated.topThreats.slice(0, limit).map((t) => {
    const meta = THREATS.find((x) => x.id === t.threatId);
    return {
      threatId: t.threatId,
      name: meta?.name ?? t.threatId,
      shortLabel: meta?.shortLabel ?? meta?.name ?? t.threatId,
      cells: framework.domains.map((d) =>
        cellExposureMulti(framework, input, t.threatId, d.id, mode),
      ),
    };
  });

  return { columns, rows };
}
